import { useRef, useEffect, useMemo, useState, useCallback, type RefObject } from "react";
import type { EventId, PlanEvent, Task, TaskId } from "../../../../shared/types";
import { CATEGORY_DOT_COLORS, STATUS_LABELS, STATUS_CYCLE, PRIORITY_LABELS, PRIORITY_COLORS } from "../../../../shared/types";
import type { Priority } from "../../../../shared/types";
import { ContextMenu } from "../../../../ui/atoms/ContextMenu";
import { updateTask, deleteTask } from "../../../tasks/store/task-store";

const PRIORITY_CYCLE: Record<Priority, Priority> = {
  low: "normal",
  normal: "urgent",
  urgent: "low",
};
import type { FormAnchor } from "../../hooks/use-week-calendar-state";
import type { SlotCreationState } from "../../hooks/use-slot-creation";
import type {
  DraggingState,
  ResizingState,
} from "../../hooks/drag-drop-helpers";
import { getNowIndicatorOffset, computeOverlapColumns } from "../../utils/time-positioning";
import { TIME_COL_W, HOUR_HEIGHT_PX, DAY_START_HOUR } from "../../utils/time-constants";
import { minutesToTime } from "../../hooks/drag-drop-helpers";
import { DayColumn } from "./DayColumn";
import { WeekGridHoursColumn } from "./WeekGridHoursColumn";
import { WeekGridGhostBlock } from "./WeekGridGhostBlock";

interface WeekGridTimeGridProps {
  readonly days: readonly string[];
  readonly today: string;
  readonly now: Date;
  readonly eventsByDate: ReadonlyMap<string, PlanEvent[]>;
  readonly tasksByDate: ReadonlyMap<string, Task[]>;
  readonly dragging: DraggingState | null;
  readonly resizing: ResizingState | null;
  readonly onEventClick?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onEventLongPress?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onEventContextMenu?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onSlotClick?: (
    date: string,
    startTime: string,
    endTime: string,
    anchor?: FormAnchor,
  ) => void;
  readonly onDragStart?: (
    event: PlanEvent,
    mouseYInColumn: number,
    clientX: number,
    clientY: number,
  ) => void;
  readonly onResizeStart?: (
    event: PlanEvent,
    clientY: number,
    edge: "top" | "bottom",
  ) => void;
  readonly justDraggedRef: RefObject<boolean>;
  readonly slotCreation: SlotCreationState | null;
  readonly onSlotCreationMouseDown?: (
    date: string,
    mouseY: number,
    columnEl: HTMLDivElement,
  ) => void;
  readonly onDayContextMenu?: (
    date: string,
    time: string,
    anchorRect: DOMRect,
  ) => void;
  readonly unlinkedTasksByDate?: ReadonlyMap<string, Task[]>;
  readonly tasksCollapsed?: boolean;
  readonly onCycleTaskStatus?: (id: TaskId) => void;
  readonly onTaskClick?: (task: Task) => void;
}

export function WeekGridTimeGrid({
  days,
  today,
  now,
  eventsByDate,
  tasksByDate,
  dragging,
  resizing,
  onEventClick,
  onEventLongPress,
  onEventContextMenu,
  onSlotClick,
  onDragStart,
  onResizeStart,
  justDraggedRef,
  slotCreation,
  onSlotCreationMouseDown,
  onDayContextMenu,
  unlinkedTasksByDate,
  tasksCollapsed,
  onCycleTaskStatus,
  onTaskClick,
}: WeekGridTimeGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [taskMenuState, setTaskMenuState] = useState<{ task: Task; anchorRect: DOMRect } | null>(null);

  const handleTaskContextMenu = useCallback(
    (e: React.MouseEvent, task: Task) => {
      e.preventDefault();
      e.stopPropagation();
      setTaskMenuState({ task, anchorRect: new DOMRect(e.clientX, e.clientY, 0, 0) });
    },
    [],
  );

  const buildTaskMenuItems = useCallback(
    (task: Task) => {
      const nextStatus = STATUS_CYCLE[task.status];
      const nextPriority = PRIORITY_CYCLE[task.priority];
      return [
        {
          label: `Statut : ${STATUS_LABELS[task.status]} → ${STATUS_LABELS[nextStatus]}`,
          onClick: () => onCycleTaskStatus?.(task.id),
        },
        {
          label: `Priorité : ${PRIORITY_LABELS[task.priority]} → ${PRIORITY_LABELS[nextPriority]}`,
          onClick: () => updateTask(task.id, { priority: nextPriority }),
        },
        {
          label: "Supprimer",
          danger: true,
          separator: true,
          onClick: () => deleteTask(task.id),
        },
      ];
    },
    [onCycleTaskStatus],
  );

  const linkedCountByEvent = useMemo(() => {
    const map = new Map<EventId, number>();
    for (const [, tasks] of tasksByDate) {
      for (const t of tasks) {
        if (t.eventId && !t.archived) {
          map.set(t.eventId, (map.get(t.eventId) ?? 0) + 1);
        }
      }
    }
    return map;
  }, [tasksByDate]);

  const AUTO_RETURN_DELAY = 30;
  const SCROLL_THRESHOLD = 150;
  const [showScrollToNow, setShowScrollToNow] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_RETURN_DELAY);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ignoreScrollUntilRef = useRef(0);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const scrollToNow = useCallback(() => {
    ignoreScrollUntilRef.current = Date.now() + 1000;
    setShowScrollToNow(false);
    setCountdown(AUTO_RETURN_DELAY);
    clearCountdown();
    const liveNow = new Date();
    const offset = getNowIndicatorOffset(liveNow);
    const el = scrollRef.current;
    if (offset !== null && el) {
      const target = Math.max(offset - 120, 0);
      const distance = Math.abs(el.scrollTop - target);
      if (distance > 800) {
        // Saut rapide près de la cible, puis smooth pour les derniers px
        el.scrollTo({ top: target + (el.scrollTop > target ? 400 : -400), behavior: "instant" });
        requestAnimationFrame(() => {
          el.scrollTo({ top: target, behavior: "smooth" });
        });
      } else {
        el.scrollTo({ top: target, behavior: "smooth" });
      }
    }
  }, [clearCountdown]);

  // Vérifie si l'utilisateur a scrollé loin de "maintenant"
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      if (Date.now() < ignoreScrollUntilRef.current) return;
      if (!days.includes(today)) {
        if (showScrollToNow) {
          setShowScrollToNow(false);
          clearCountdown();
        }
        return;
      }
      const offset = getNowIndicatorOffset(now);
      if (offset === null) return;
      const idealTop = Math.max(offset - 120, 0);
      const distance = Math.abs(el.scrollTop - idealTop);
      const isAway = distance > SCROLL_THRESHOLD;

      if (isAway && !showScrollToNow) {
        setShowScrollToNow(true);
        setCountdown(AUTO_RETURN_DELAY);
        clearCountdown();
        countdownRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              scrollToNow();
              return AUTO_RETURN_DELAY;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (!isAway && showScrollToNow) {
        setShowScrollToNow(false);
        setCountdown(AUTO_RETURN_DELAY);
        clearCountdown();
      }
    };
    el.addEventListener("scroll", check, { passive: true });
    return () => el.removeEventListener("scroll", check);
  }, [days, today, now, showScrollToNow, clearCountdown, scrollToNow]);

  // Auto-scroll initial vers maintenant
  useEffect(() => {
    if (!days.includes(today)) return;
    const offset = getNowIndicatorOffset(now);
    if (offset !== null && scrollRef.current) {
      scrollRef.current.scrollTop = Math.max(offset - 120, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days[0]]);

  // Cleanup countdown on unmount
  useEffect(() => clearCountdown, [clearCountdown]);

  const hasAnyUnlinkedTasks = unlinkedTasksByDate ? Array.from(unlinkedTasksByDate.values()).some((t) => t.length > 0) : false;

  return (
    <div
      ref={scrollRef}
      className="overflow-y-auto flex-1 min-h-0 scrollbar-hide relative"
    >
      {/* Tâches du jour — sticky en haut de la zone scroll */}
      {hasAnyUnlinkedTasks && !tasksCollapsed && (
        <div
          className="sticky top-0 z-10 grid pointer-events-none"
          style={{ gridTemplateColumns: `${TIME_COL_W}px repeat(${days.length}, 1fr)` }}
        >
          <div className="border-r border-border/15" />
          {days.map((day) => {
            const tasks = unlinkedTasksByDate?.get(day) ?? [];
            if (tasks.length === 0) return <div key={day} />;
            return (
              <div key={day} className="px-0.5 py-0.5 pointer-events-auto bg-surface overflow-hidden">
                <div className="bg-surface-alt/50 rounded-md px-0.5 py-0.5 flex flex-col gap-0.5">
                  {tasks.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onTaskClick) { onTaskClick(t); } else { onCycleTaskStatus?.(t.id); }
                      }}
                      onContextMenu={(e) => handleTaskContextMenu(e, t)}
                      className="w-full flex items-center gap-1 rounded px-1 py-px hover:bg-surface-alt transition-colors group"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${CATEGORY_DOT_COLORS[t.category]}`} />
                      <span className="text-[10px] text-ink-muted truncate flex-1 text-left">{t.title}</span>
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCycleTaskStatus?.(t.id);
                        }}
                        className={`text-[8px] shrink-0 hover:text-accent cursor-pointer transition-colors ${PRIORITY_COLORS[t.priority]}`}
                      >
                        {STATUS_LABELS[t.status]}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {taskMenuState && (
        <ContextMenu
          items={buildTaskMenuItems(taskMenuState.task)}
          anchorRect={taskMenuState.anchorRect}
          onClose={() => setTaskMenuState(null)}
        />
      )}

      <div
        className="grid relative"
        style={{
          gridTemplateColumns: `${TIME_COL_W}px repeat(${days.length}, 1fr)`,
        }}
      >
        <WeekGridHoursColumn />

        {days.map((day) => (
          <DayColumn
            key={day}
            date={day}
            today={today}
            now={now}
            events={(eventsByDate.get(day) ?? [])}
            linkedCountByEvent={linkedCountByEvent}
            draggingEventId={dragging?.event.id ?? null}
            resizingEventId={resizing?.event.date === day ? resizing.event.id : null}
            resizingHeightPx={resizing?.event.date === day ? resizing.ghostHeightPx : undefined}
            resizingTopPx={resizing?.event.date === day ? resizing.ghostTopPx : undefined}
            resizingStartMinutes={resizing?.event.date === day ? resizing.newStartMinutes : undefined}
            resizingEndMinutes={resizing?.event.date === day ? resizing.newEndMinutes : undefined}
            resizingEdge={resizing?.event.date === day ? resizing.edge : undefined}
            onEventClick={onEventClick ? (event, anchorRect) => {
              if (justDraggedRef.current) return;
              onEventClick(event, anchorRect);
            } : undefined}
            onEventLongPress={onEventLongPress}
            onEventContextMenu={onEventContextMenu}
            ghostDragEvent={
              dragging && dragging.ghostDate === day
                ? {
                    startTime: minutesToTime(Math.round((dragging.ghostTopPx / HOUR_HEIGHT_PX) * 60)),
                    endTime: minutesToTime(Math.round((dragging.ghostTopPx / HOUR_HEIGHT_PX) * 60) + dragging.durationMinutes),
                    eventId: dragging.event.id,
                  }
                : null
            }
            slotCreationPreview={
              slotCreation && slotCreation.date === day
                ? {
                    topPx: Math.min(slotCreation.startPx, slotCreation.endPx),
                    heightPx: Math.abs(slotCreation.endPx - slotCreation.startPx),
                  }
                : null
            }
            onSlotClick={onSlotClick ? (start, end, anchor) => {
              if (justDraggedRef.current) return;
              onSlotClick(day, start, end, anchor);
            } : undefined}
            onSlotCreationMouseDown={onSlotCreationMouseDown}
            onDragStart={onDragStart}
            onResizeStart={onResizeStart}
            onDayContextMenu={onDayContextMenu}
          />
        ))}

        {dragging && (() => {
          const ghostStartMin = DAY_START_HOUR * 60 + (dragging.ghostTopPx / HOUR_HEIGHT_PX) * 60;
          const ghostEndMin = ghostStartMin + dragging.durationMinutes;
          const ghostFake: PlanEvent = {
            id: "__ghost_drag__" as EventId,
            title: "",
            category: "personal",
            date: dragging.ghostDate,
            startTime: minutesToTime(Math.round(ghostStartMin)),
            endTime: minutesToTime(Math.round(ghostEndMin)),
            createdAt: 0,
            description: "",
            notes: "",
            tags: [],
            recurrence: null,
            reminderMinutes: null,
            reminderTime: null,
            reminderType: null,
          };
          const dayEvents = (eventsByDate.get(dragging.ghostDate) ?? [])
            .filter((e) => !e.allDay && e.id !== dragging.event.id);
          const withGhost = [...dayEvents, ghostFake];
          const ghostOverlapMap = computeOverlapColumns(withGhost);
          const ghostOverlap = ghostOverlapMap.get("__ghost_drag__" as EventId);
          return (
            <WeekGridGhostBlock
              ghostDate={dragging.ghostDate}
              ghostTopPx={dragging.ghostTopPx}
              durationMinutes={dragging.durationMinutes}
              event={dragging.event}
              days={days}
              column={ghostOverlap?.column ?? 0}
              totalColumns={ghostOverlap?.totalColumns ?? 1}
            />
          );
        })()}
      </div>

      {/* Bouton "Maintenant" — sticky en bas, visible quand l'utilisateur a scrollé */}
      {showScrollToNow && (
        <div className="sticky bottom-0 z-20 pointer-events-none" style={{ gridColumn: "1 / -1" }}>
          <div className="flex justify-start pl-1 pb-1">
            <button
              type="button"
              onClick={scrollToNow}
              className="pointer-events-auto flex items-center gap-1.5 px-2 py-1 bg-surface-alt border border-border/30 text-ink-muted text-[10px] font-medium rounded-md shadow-sm hover:text-accent hover:border-accent/30 transition-colors"
              title="Revenir à l'heure actuelle"
            >
              Maintenant
              <span className="text-ink-faint tabular-nums">{countdown}s</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
